'use client';

import {useQuery, type UseQueryResult} from '@tanstack/react-query';
import {tenantKeys} from './tenantKeys';
import {getIdentityApi} from '../api/identity-api';
import {useApiToken} from '@/matic/hooks/useApiClient';
import type {Tenant} from '../api/types';

/**
 * Fetch a single tenant by ID
 *
 * @param tenantId - Tenant ID (UUID)
 * @returns UseQueryResult<Tenant, Error>
 *
 * @example
 * ```typescript
 * const {data: tenant, isLoading, error} = useTenant(tenantId);
 *
 * if (isLoading) return <div>Loading tenant...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 *
 * return <div>Welcome to {tenant.name}</div>;
 * ```
 */
export function useTenant(tenantId?: string): UseQueryResult<Tenant> {
  const token = useApiToken();
  const api = getIdentityApi(token);

  return useQuery({
    queryKey: tenantKeys.detail(tenantId ?? ''),
    async queryFn() {
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      return api.getTenant(tenantId);
    },
    enabled: Boolean(tenantId && token),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
